import type { ProductPresentationFact } from "./productPresentation";

export type ProductPhotoSource = {
  image?: unknown;
  imageBig?: unknown;
  images?: Array<{ image?: unknown; imageBig?: unknown }> | null;
};

export type OpticianClipboardContentInput = {
  productName?: unknown;
  facts?: readonly ProductPresentationFact[];
  questions: readonly string[];
  photoUrl?: unknown;
};

export type OpticianClipboardContent = {
  text: string;
  html: string;
  /** Exactly the three questions: no product summary, facts, URL or photo prose. */
  questionsText: string;
  photoUrl: string | null;
};

export type OpticianClipboardFallback = {
  /** Explicit action only: this text can be copied without a photo. */
  questionsText: string;
  /** Explicit action only: rich text without the embedded photo. */
  richTextHtml: string;
  /** The UI can offer the actual source photo as a download. */
  photoUrl: string | null;
};

export type OpticianClipboardFailureCode =
  | "SSR"
  | "INSECURE_CONTEXT"
  | "CLIPBOARD_UNSUPPORTED"
  | "IMAGE_UNSUPPORTED"
  | "PHOTO_UNAVAILABLE"
  | "WRITE_FAILED";

export type OpticianClipboardResult =
  | { ok: true; formats: ["text/plain", "text/html", "image/png"] }
  | {
      ok: false;
      code: OpticianClipboardFailureCode;
      fallback: OpticianClipboardFallback;
    };

export type OpticianFallbackCopyResult =
  | { ok: true }
  | {
      ok: false;
      code:
        | "SSR"
        | "INSECURE_CONTEXT"
        | "CLIPBOARD_UNSUPPORTED"
        | "WRITE_FAILED";
    };

type ClipboardAvailabilityFailure =
  | "SSR"
  | "INSECURE_CONTEXT"
  | "CLIPBOARD_UNSUPPORTED";

type ClipboardItemConstructor = typeof ClipboardItem & {
  supports?: (mimeType: string) => boolean;
};

const HTML_ESCAPE: Record<string, string> = {
  "&": "&amp;",
  "<": "&lt;",
  ">": "&gt;",
  '"': "&quot;",
  "'": "&#39;",
};

function escapeHtml(value: unknown): string {
  return String(value ?? "").replace(
    /[&<>"']/g,
    (character) => HTML_ESCAPE[character]
  );
}

function cleanText(value: unknown): string {
  return String(value ?? "")
    .replace(/\s+/g, " ")
    .trim();
}

/** Accept only HTTP(S) product images; data/javascript URLs never enter HTML. */
export function safeProductPhotoUrl(value: unknown): string | null {
  const raw = cleanText(value);
  if (!raw || typeof window === "undefined") return null;
  try {
    const parsed = new URL(raw, window.location.href);
    if (parsed.protocol !== "https:" && parsed.protocol !== "http:")
      return null;
    parsed.hash = "";
    return parsed.toString();
  } catch {
    return null;
  }
}

/** Follow the PDP gallery's real-photo preference: main image, then gallery. */
export function resolveProductPhotoUrl(
  product: ProductPhotoSource | null | undefined
): string | null {
  if (!product) return null;
  const candidates = [
    product.image,
    product.imageBig,
    ...(Array.isArray(product.images)
      ? product.images.flatMap((image) => [image?.image, image?.imageBig])
      : []),
  ];
  for (const candidate of candidates) {
    const safe = safeProductPhotoUrl(candidate);
    if (safe) return safe;
  }
  return null;
}

/** The single questions-only payload: both fallback controls copy exactly this. */
function questionsOnlyText(questions: readonly string[]): string {
  return `Questions for my optician:\n${questions
    .map((question, index) => `${index + 1}. ${cleanText(question)}`)
    .join("\n")}`.trim();
}

function questionsBlock(productName: string, questionsOnly: string): string {
  return productName ? `${productName}\n\n${questionsOnly}` : questionsOnly;
}

function factsText(facts: readonly ProductPresentationFact[]): string {
  return facts
    .map((fact) => `${fact.label}: ${cleanText(fact.value)}`)
    .join("\n");
}

function contentHtml(
  productName: string,
  facts: readonly ProductPresentationFact[],
  questions: readonly string[],
  imageDataUrl?: string,
  photoUrl?: string | null
): string {
  const factMarkup = facts.length
    ? `<dl>${facts
        .map(
          (fact) =>
            `<dt>${escapeHtml(fact.label)}</dt><dd>${escapeHtml(
              cleanText(fact.value)
            )}</dd>`
        )
        .join("")}</dl>`
    : "";
  const questionMarkup = questions
    .map((question) => `<li>${escapeHtml(cleanText(question))}</li>`)
    .join("");
  const imageMarkup = imageDataUrl
    ? `<p><img src="${imageDataUrl}" alt="${escapeHtml(
        productName || "Product photo"
      )}" /></p>`
    : photoUrl
    ? `<p><a href="${escapeHtml(photoUrl)}">Product photo</a></p>`
    : "";

  return `<article><h1>${escapeHtml(
    productName || "Eyewear frame"
  )}</h1>${imageMarkup}${factMarkup}<h2>Questions for my optician</h2><ol>${questionMarkup}</ol></article>`;
}

/** Build safe text and HTML. The photo is embedded only by the main copy call. */
export function buildOpticianClipboardContent(
  input: OpticianClipboardContentInput
): OpticianClipboardContent {
  const productName = cleanText(input.productName);
  const facts = Array.isArray(input.facts) ? input.facts : [];
  const questions = (Array.isArray(input.questions) ? input.questions : [])
    .map(cleanText)
    .filter(Boolean);
  const photoUrl = safeProductPhotoUrl(input.photoUrl);
  const questionsOnly = questionsOnlyText(questions);
  const productFacts = factsText(facts);
  const text = [productFacts, questionsBlock(productName, questionsOnly)]
    .filter(Boolean)
    .join("\n\n");

  return {
    text,
    html: contentHtml(productName, facts, questions, undefined, photoUrl),
    questionsText: questionsOnly,
    photoUrl,
  };
}

function fallbackFor(
  content: OpticianClipboardContent
): OpticianClipboardFallback {
  return {
    questionsText: content.questionsText,
    richTextHtml: content.html,
    photoUrl: content.photoUrl,
  };
}

function canUseClipboard(): ClipboardAvailabilityFailure | null {
  if (typeof window === "undefined" || typeof navigator === "undefined")
    return "SSR";
  if (!window.isSecureContext) return "INSECURE_CONTEXT";
  if (!navigator.clipboard?.write) return "CLIPBOARD_UNSUPPORTED";
  return null;
}

function clipboardItemConstructor(): ClipboardItemConstructor | null {
  if (typeof ClipboardItem !== "undefined")
    return ClipboardItem as ClipboardItemConstructor;
  return null;
}

function pngSupported(
  ClipboardItemConstructor: ClipboardItemConstructor
): boolean {
  return (
    typeof ClipboardItemConstructor.supports !== "function" ||
    ClipboardItemConstructor.supports("image/png")
  );
}

/**
 * Give one promise its own rejection observer and hand the same promise back.
 * A clipboard implementation does not consistently consume every promise it
 * was given — a denied `write()` rejects before the photo resolves — so each
 * promise built here, including the ones derived from the shared payload, needs
 * its own handler. Attaching it does not settle or replace the promise, so the
 * original rejection still reaches the ClipboardItem and the classification
 * below; it only stops a recoverable failure becoming an unhandled rejection.
 */
function observeRejection<T>(promise: Promise<T>): Promise<T> {
  promise.catch(() => {});
  return promise;
}

const PHOTO_FETCH_TIMEOUT_MS = 15_000;

function fetchPhoto(
  photoUrl: string,
  controller: AbortController
): Promise<Blob> {
  const timeout = window.setTimeout(
    () => controller.abort(),
    PHOTO_FETCH_TIMEOUT_MS
  );
  return fetch(photoUrl, {
    method: "GET",
    mode: "cors",
    credentials: "same-origin",
    headers: { Accept: "image/avif,image/webp,image/*" },
    signal: controller.signal,
  })
    .then((response) => {
      if (!response.ok) throw new Error("Product photo could not be fetched.");
      const type = String(
        response.headers.get("content-type") || ""
      ).toLowerCase();
      if (!type.startsWith("image/"))
        throw new Error("Product photo is not an image.");
      return response.blob();
    })
    .finally(() => window.clearTimeout(timeout));
}

/**
 * Copy text, embedded HTML, and image/png as one ClipboardItem. Call this
 * directly from the button handler: promise-valued item data preserves the
 * user activation Safari requires while the image fetch/conversion completes.
 */
export async function copyOpticianClipboard(
  content: OpticianClipboardContent
): Promise<OpticianClipboardResult> {
  const fallback = fallbackFor(content);
  const unavailable = canUseClipboard();
  if (unavailable) return { ok: false, code: unavailable, fallback };
  if (!content.photoUrl)
    return { ok: false, code: "PHOTO_UNAVAILABLE", fallback };

  const ClipboardItemConstructor = clipboardItemConstructor();
  if (!ClipboardItemConstructor) {
    return { ok: false, code: "IMAGE_UNSUPPORTED", fallback };
  }
  if (!pngSupported(ClipboardItemConstructor)) {
    return { ok: false, code: "IMAGE_UNSUPPORTED", fallback };
  }

  let imageError = false;
  const photoController = new AbortController();
  const payload = observeRejection(
    fetchPhoto(content.photoUrl, photoController)
      .then(async (photo) => {
        const { buildClipboardPhotoPayload } = await import(
          "./opticianClipboardImage"
        );
        return buildClipboardPhotoPayload(photo, content.html);
      })
      .catch((error) => {
        imageError = true;
        throw error;
      })
  );
  // `png` and `html` are separate promises: each rejects on its own when the
  // photo fails or is aborted, so each needs its own observer, not only the
  // shared payload. The observed promises are still what ClipboardItem receives.
  const png = observeRejection(payload.then(({ png }) => png));
  const html = observeRejection(payload.then(({ html }) => html));

  try {
    // Do not await any image work before this call: it must remain inside the
    // original click's activation window. No previous clipboard content is read.
    const item = new ClipboardItemConstructor({
      "text/plain": Promise.resolve(
        new Blob([content.text], { type: "text/plain" })
      ),
      "text/html": html,
      "image/png": png,
    } as unknown as Record<string, Blob>);
    await navigator.clipboard.write([item]);
    return { ok: true, formats: ["text/plain", "text/html", "image/png"] };
  } catch {
    // Permission and constructor failures do not need the still-pending photo.
    // Abort it now rather than leaving a slow CDN request alive for 15 seconds.
    photoController.abort();
    return {
      ok: false,
      code: imageError ? "PHOTO_UNAVAILABLE" : "WRITE_FAILED",
      fallback,
    };
  }
}

/** Explicit UI fallback: copy questions as plain text only. */
export async function copyOpticianQuestionsOnly(
  questions: string
): Promise<OpticianFallbackCopyResult> {
  if (typeof window === "undefined" || typeof navigator === "undefined")
    return { ok: false, code: "SSR" };
  if (!window.isSecureContext) return { ok: false, code: "INSECURE_CONTEXT" };
  if (!navigator.clipboard?.writeText)
    return { ok: false, code: "CLIPBOARD_UNSUPPORTED" };
  try {
    await navigator.clipboard.writeText(questions);
    return { ok: true };
  } catch {
    return { ok: false, code: "WRITE_FAILED" };
  }
}

/** Explicit UI fallback: write rich text after the user chooses a no-photo copy. */
export async function copyOpticianRichTextFallback(
  content: OpticianClipboardContent
): Promise<OpticianFallbackCopyResult> {
  const unavailable = canUseClipboard();
  if (unavailable) return { ok: false, code: unavailable };
  const ClipboardItemConstructor = clipboardItemConstructor();
  if (!ClipboardItemConstructor)
    return { ok: false, code: "CLIPBOARD_UNSUPPORTED" };
  try {
    const item = new ClipboardItemConstructor({
      "text/plain": new Blob([content.text], { type: "text/plain" }),
      "text/html": new Blob([content.html], { type: "text/html" }),
    });
    await navigator.clipboard.write([item]);
    return { ok: true };
  } catch {
    return { ok: false, code: "WRITE_FAILED" };
  }
}
